// DockPanel (DESIGN §3.1.10): Rahmen rechts 400 unter der Topbar, `card`, linke Ecken 16,
// `shadow-dock`; ab `md` überlagert, ab `2xl` schiebt er den Inhalt (AppShell). Kein Fokus-Trap;
// Esc schließt, Fokus springt beim Öffnen in den Kopf und beim Schließen zurück zum Auslöser.
// Unter `md` rendert die Plattform statt des Docks die Chat-Route (ENTSCHEIDUNG design-kritik).
import { ArrowLeft, ArrowsOut, X } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode, useEffect, useRef } from 'react';
import { cn } from '../kern/cn';
import { DURATION, EASE } from '../kern/tokens';
import { ShellIconButton } from './parts';

export interface DockFrameProps {
  title: ReactNode;
  /** Zurück ab Ebene 2 (Unterhaltung, Thread). */
  onBack?: () => void;
  backLabel?: string;
  /** „Open in Chat“ → ganze Seite. */
  expandHref?: string;
  expandLabel?: string;
  onClose: () => void;
  /** Weitere Icon-Knöpfe im Kopf. */
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}

/** Kopf, Inhalt und Fuß eines Dock-Panels; Panels aus der Registry rendern sich darin. */
export function DockFrame({
  title,
  onBack,
  backLabel = 'Back',
  expandHref,
  expandLabel = 'Open in Chat',
  onClose,
  actions,
  footer,
  children,
}: DockFrameProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-14 shrink-0 items-center gap-1 px-2">
        {onBack ? (
          <ShellIconButton icon={ArrowLeft} label={backLabel} tone="card" onClick={onBack} />
        ) : null}
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="min-w-0 flex-1 truncate px-2 text-label-strong text-fg outline-none"
        >
          {title}
        </h2>
        {actions}
        {expandHref ? (
          <ShellIconButton icon={ArrowsOut} label={expandLabel} tone="card" href={expandHref} />
        ) : null}
        <ShellIconButton icon={X} label="Close" tone="card" onClick={onClose} />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
      {footer ? <div className="shrink-0 p-3">{footer}</div> : null}
    </div>
  );
}

export interface DockPanelProps {
  open: boolean;
  onClose: () => void;
  /** Landmark-Name, z. B. „Chat“. */
  label: string;
  children: ReactNode;
  className?: string;
}

/** Der Rahmen selbst (aside). Esc schließt, außer ein Popover/Menü darin ist offen. */
export function DockPanel({ open, onClose, label, children, className }: DockPanelProps) {
  const returnFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    returnFocus.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (
        document.querySelector(
          '[role="dialog"][data-state="open"], [role="menu"][data-state="open"]',
        )
      )
        return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const target = returnFocus.current;
      if (target?.isConnected) target.focus({ preventScroll: true });
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.aside
          key="dock"
          role="complementary"
          aria-label={label}
          data-slot="dock"
          initial={{ x: '100%' }}
          animate={{ x: 0, transition: { duration: DURATION.slow / 1000, ease: EASE } }}
          exit={{ x: '100%', transition: { duration: DURATION.base / 1000, ease: EASE } }}
          className={cn(
            'fixed top-topbar right-0 bottom-0 z-(--bar-z-dock) hidden w-dock flex-col overflow-hidden rounded-l-xl bg-card shadow-dock md:flex',
            className,
          )}
        >
          {children}
        </motion.aside>
      ) : null}
    </AnimatePresence>
  );
}
